/**
 * Order Reception ("Recepción") API client.
 *
 * CSRF/JSON pattern mirrors inventoryCount.ts: read the `pacebi_csrf` cookie
 * and echo it back via X-CSRF-Token on every state-changing request.
 *
 * NOTE on idempotency_key: like the physcount handlers, handlers_reception.go
 * does NOT declare an IdempotencyKey field on its request-body structs, and
 * readJSON() there decodes with json.Decoder.DisallowUnknownFields(). An
 * idempotency_key field on openReception's or scanReception's JSON body
 * would therefore fail the whole request with 400 "bad json" instead of
 * being ignored — deliberately omitted here, same reasoning inventoryCount.ts
 * documents for the physcount endpoints.
 */
import type { ReceptionSession, ScanResult, ReconReport, PostReceptionResponse } from './types';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

/** Headers for requests with no JSON body (DELETE). */
function csrfOnlyHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = {};
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** List all reception sessions, newest first. GET /api/receptions. */
export function listReceptions(): Promise<ReceptionSession[]> {
  return getJSON<ReceptionSession[]>('/api/receptions');
}

/**
 * Open a reception session against one PO: reads the PO's still-open lines
 * live (handle-purchase-detail) and snapshots them into the session's
 * ledger. 404 when the PO isn't found; 403 without receiving:use.
 * POST /api/receptions.
 */
export async function openReception(sid: number, onum: number): Promise<ReceptionSession> {
  const res = await fetch('/api/receptions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify({ sid, onum }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as ReceptionSession;
}

/** Get one session by id. GET /api/receptions/:id. */
export function getReception(id: number): Promise<ReceptionSession> {
  return getJSON<ReceptionSession>(`/api/receptions/${id}`);
}

/** Hard-delete a session and its lines + scans. DELETE /api/receptions/:id. */
export async function deleteReception(id: number): Promise<void> {
  const res = await fetch(`/api/receptions/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: csrfOnlyHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
}

export interface ScanBody {
  device: string;
  code: string;
  bartype?: string;
  /** Keyed-quantity override (packs to add); omitted = server adds qty_per_scan. */
  qty?: number;
}

/**
 * Record one scan: resolves `code` via handle-barcode-resolve and
 * accumulates it into the device's bucket. `resolved:false` means the code
 * matched nothing — the caller shows "No reconocido" and nothing was
 * recorded server-side. POST /api/receptions/:id/scan.
 */
export async function scanReception(id: number, body: ScanBody): Promise<ScanResult> {
  const res = await fetch(`/api/receptions/${id}/scan`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as ScanResult;
}

/**
 * Join every device's scans against the expected ledger: per-line
 * exact/short/over classification, off-PO "unexpected" scans, and the
 * session summary. Cost/value fields are null without cost:visible.
 * GET /api/receptions/:id/reconcile.
 */
export function reconcileReception(id: number): Promise<ReconReport> {
  return getJSON<ReconReport>(`/api/receptions/${id}/reconcile`);
}

/**
 * Thrown by postReception when writes are disabled. The server deliberately
 * 404s rather than 403 for this route (handlers_reception.go — same
 * writes_enabled gate as every other write route) so the SPA can key off it
 * to hide the "Postear recepción" controls entirely — same convention
 * inventoryCount.ts's postSession uses for physcount, just as a typed error
 * here instead of a bare Error(message).
 */
export class WritesDisabledError extends Error {
  constructor() {
    super('writes_disabled');
    this.name = 'WritesDisabledError';
  }
}

/**
 * Receive a session's on-PO scanned lines into the ERP via paceWriteServ's
 * post-receiving verb (recvpost.p, Task 11/13). Empty body — the server
 * derives which lines are postable (scanned, not already posted/posting)
 * itself; there is no mode/uncounted override like physcount's post. "Recibir
 * restantes" (retrying after a partial post) is the same call — only
 * non-terminal, actually-scanned lines are re-sent, so it's safe to call
 * repeatedly. POST /api/receptions/:id/post.
 */
export async function postReception(id: number): Promise<PostReceptionResponse> {
  const res = await fetch(`/api/receptions/${id}/post`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify({}),
  });
  if (res.status === 404) throw new WritesDisabledError();
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as PostReceptionResponse;
}
